<template>
  <div class="maintainance-task-detail-view">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('maintainanceTaskDetailView.page.title')"
      :subtitle="$t('maintainanceTaskDetailView.page.subtitle')">
      <template #extra>
        <el-button icon="el-icon-back" @click="goBack">
          {{ $t('maintainanceTaskDetailView.back') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- 详情信息面板 -->
    <div class="ds-panel ds-panel--detail">
      <div class="ds-panel__header">
        <span class="ds-panel__title">{{ $t('maintainanceTaskDetailView.basicInfo') }}</span>
      </div>
      <div v-loading="loading">
        <el-row :gutter="16">
          <el-col :xs="24" :sm="12" :md="8" :lg="6">
            <div class="ds-field">
              <div class="ds-field__label">{{ $t('maintainanceTaskDetailView.fields.taskDetailId') }}</div>
              <div class="ds-field__value ds-num">{{ detail.taskDetailId || '-' }}</div>
            </div>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8" :lg="6">
            <div class="ds-field">
              <div class="ds-field__label">{{ $t('maintainanceTaskDetailView.fields.machineName') }}</div>
              <div class="ds-field__value">{{ detail.machineName || '-' }}</div>
            </div>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8" :lg="6">
            <div class="ds-field">
              <div class="ds-field__label">{{ $t('maintainanceTaskDetailView.fields.planUserName') }}</div>
              <div class="ds-field__value">{{ detail.planUserName || '-' }}</div>
            </div>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8" :lg="6">
            <div class="ds-field">
              <div class="ds-field__label">{{ $t('maintainanceTaskDetailView.fields.stateName') }}</div>
              <div class="ds-field__value">
                <ds-status-tag :type="resolveState(detail)" :label="detail.stateName || '-'" />
              </div>
            </div>
          </el-col>
        </el-row>

        <el-row :gutter="16">
          <el-col :xs="24" :sm="12" :md="8" :lg="6">
            <div class="ds-field">
              <div class="ds-field__label">{{ $t('maintainanceTaskDetailView.fields.planTime') }}</div>
              <div class="ds-field__value ds-num">{{ formatPlanTime(detail.planInsTime, detail.planEndTime) }}</div>
            </div>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8" :lg="6">
            <div class="ds-field">
              <div class="ds-field__label">{{ $t('maintainanceTaskDetailView.fields.actUserName') }}</div>
              <div class="ds-field__value">{{ detail.actUserName || '-' }}</div>
            </div>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8" :lg="6">
            <div class="ds-field">
              <div class="ds-field__label">{{ $t('maintainanceTaskDetailView.fields.inspectionTime') }}</div>
              <div class="ds-field__value ds-num">{{ formatDateTimeSec(detail.inspectionTime) }}</div>
            </div>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8" :lg="6">
            <div class="ds-field">
              <div class="ds-field__label">{{ $t('maintainanceTaskDetailView.fields.planName') }}</div>
              <div class="ds-field__value">{{ detail.planName || '-' }}</div>
            </div>
          </el-col>
        </el-row>

        <el-row :gutter="16">
          <el-col :xs="24" :sm="12" :md="8" :lg="6">
            <div class="ds-field">
              <div class="ds-field__label">{{ $t('maintainanceTaskDetailView.fields.standardName') }}</div>
              <div class="ds-field__value">{{ detail.standardName || '-' }}</div>
            </div>
          </el-col>
        </el-row>
      </div>
    </div>

    <!-- 保养图片面板 -->
    <div class="ds-panel">
      <div class="ds-panel__header">
        <span class="ds-panel__title">{{ $t('maintainanceTaskDetailView.photosTitle') }}</span>
        <span class="ds-panel__extra">{{ photoList.length }}</span>
      </div>
      <div class="ds-panel__body">
        <div v-if="photoList.length" class="ds-photo-list">
          <el-image
            v-for="(photo, index) in photoList"
            :key="index"
            :src="photo"
            :preview-src-list="photoList"
            fit="cover"
            class="ds-photo-item"
          />
        </div>
        <ds-empty-state v-else :description="$t('maintainanceTaskDetailView.noPhotos')" />
      </div>
    </div>

    <!-- 保养说明面板 -->
    <div class="ds-panel">
      <div class="ds-panel__header">
        <span class="ds-panel__title">{{ $t('maintainanceTaskDetailView.descriptionTitle') }}</span>
      </div>
      <div class="ds-panel__body">
        <div class="ds-description">
          {{ detail.description || $t('maintainanceTaskDetailView.noDescription') }}
        </div>
      </div>
    </div>

    <view-image ref="viewImage" />
  </div>
</template>

<script>
import { getMaintainanceTaskDetailList } from '@/api/inspection/maintainanceTaskDetailsApi'
import ViewImage from '@/components/system/viewImage'
import { messages } from './maintainanceTaskDetailViewLang'
import { formatDateTimeSec } from '@/utils/format'
import { resolveStatus } from '@/utils/statusMap'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsEmptyState from '@/components/ds/ds-empty-state.vue'

export default {
  name: 'MaintainanceTaskDetailView',
  components: {
    ViewImage,
    DsPageHeader,
    DsStatusTag,
    DsEmptyState
  },
  i18n: {
    messages
  },
  data() {
    return {
      detail: {
        taskDetailId: '',
        machineName: '',
        planName: '',
        standardName: '',
        planUserName: '',
        planInsTime: '',
        planEndTime: '',
        actUserName: '',
        inspectionTime: '',
        stateName: '',
        description: '',
        photos: []
      },
      loading: false
    }
  },
  computed: {
    photoList() {
      if (!this.detail.photos || !this.detail.photos.length) {
        return []
      }
      return this.detail.photos.map(photo => photo.url).filter(Boolean)
    }
  },
  created() {
    this.fetchDetail()
  },
  methods: {
    async fetchDetail() {
      const taskDetailId = this.$route.query.taskDetailId
      if (!taskDetailId) {
        return
      }
      try {
        this.loading = true
        const params = {
          page: 1,
          row: 1,
          taskDetailId
        }
        const res = await getMaintainanceTaskDetailList(params)
        if (res && res.data && res.data.length) {
          this.detail = { ...this.detail, ...res.data[0] }
        }
      } catch (error) {
        console.error('获取保养详情失败:', error)
        this.$message.error(this.$t('maintainanceTaskDetailView.fetchError'))
      } finally {
        this.loading = false
      }
    },
    formatPlanTime(start, end) {
      if (!start && !end) {
        return '-'
      }
      if (!start) {
        return end
      }
      if (!end) {
        return start
      }
      return `${start} ~ ${end}`
    },
    formatDateTimeSec(value) {
      return formatDateTimeSec(value)
    },
    /**
     * 保养任务状态语义标签（maintainance_task 域，与巡检任务分域映射）
     */
    resolveState(row) {
      if (!row) return 'muted'
      const resolved = resolveStatus(row.state || row.taskState || row.statusCd, {
        domain: 'maintainanceTask',
        text: row.stateName || '',
        fallbackLabel: row.stateName || ''
      })
      return resolved.type
    },
    goBack() {
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.push({ path: '/pages/property/maintainanceTaskDetails' })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.maintainance-task-detail-view {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  margin-bottom: var(--ds-spacing-md);

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--ds-spacing-sm);
    padding: var(--ds-spacing-sm) var(--ds-spacing-lg);
    background: var(--ds-gray-100);
    border-bottom: 1px solid var(--ds-gray-200);
  }

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }

  &__extra {
    font-size: var(--ds-font-size-sm);
    color: var(--ds-gray-500);
  }

  &__body {
    padding: var(--ds-spacing-lg);
  }
}

/* 详情字段 */
.ds-field {
  margin-bottom: var(--ds-spacing-md);

  &__label {
    font-size: var(--ds-font-size-xs);
    color: var(--ds-gray-500);
    margin-bottom: var(--ds-spacing-xs);
  }

  &__value {
    font-size: var(--ds-font-size-sm);
    color: var(--ds-gray-800);
    line-height: 1.6;
    word-break: break-all;
  }
}

/* 保养图片列表 */
.ds-photo-list {
  display: flex;
  flex-wrap: wrap;
  gap: var(--ds-spacing-sm);
}

.ds-photo-item {
  width: 120px;
  height: 120px;
  border-radius: var(--ds-radius-sm);
  overflow: hidden;
  border: 1px solid var(--ds-gray-200);
}

/* 保养说明 */
.ds-description {
  min-height: 80px;
  line-height: 1.7;
  color: var(--ds-gray-700);
  white-space: pre-wrap;
}
</style>
